import { useValidatedState } from 'beautiful-react-hooks'
import { clamp } from 'lodash'
import { schema } from 'prose-core'
import { type EditorView } from 'prosemirror-view'
import { useContext } from 'react'
import { NodeSelection } from 'prosemirror-state'

import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { preserveSelection } from 'common/Prose/utils'
import { findParentNodeOfType } from 'prosemirror-utils'
import { AlmanacEditorStateContext } from 'stores/EditorState'
import { determineFontAttribute } from './determineFontAttribute'
import { selectionIncludesNodeType } from './selectionIncludesNodeType'

export const MAX_FONT_SIZE = 200
export const MIN_FONT_SIZE = 1

const changeFontSize = (view: EditorView, sizeChange: number, absolute = false) => {
  const {
    state,
    state: { doc, selection, schema, tr },
  } = view
  const { $from, $to } = selection

  if (selectionIncludesNodeType(schema.nodes.code_block, state)) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const $codeBlock = findParentNodeOfType(schema.nodes.code_block)(state.selection)!
    const updatedSize =
      ($codeBlock.node.marks.find((m) => m.type === schema.marks.font_size)?.attrs.size ?? 16) + sizeChange
    tr.setNodeMarkup($codeBlock.pos, undefined, $codeBlock.node.attrs, [
      ...$codeBlock.node.marks.filter((m) => m.type !== schema.marks.font_size),
      schema.marks.font_size.create({
        size: clamp(updatedSize, MIN_FONT_SIZE, MAX_FONT_SIZE),
      }),
    ])

    return tr
  }

  let from = selection.from
  let to = selection.to

  const selectionRanges: Array<{ $from: { pos: number }; $to: { pos: number } }> = [...selection.ranges]

  if (selection.empty) {
    from = $from.before()
    to = $to.after()

    selectionRanges.push({ $from: { pos: from }, $to: { pos: to } })
  }

  for (let {
    $from: { pos: from },
    $to: { pos: to },
  } of selectionRanges) {
    doc.nodesBetween(from, to, (node, pos) => {
      if (node.isText) {
        const currentSize = determineFontAttribute(view, node, pos, schema.marks.font_size)
        let updatedSize = currentSize + sizeChange
        updatedSize = clamp(updatedSize, MIN_FONT_SIZE, MAX_FONT_SIZE)
        if (absolute) updatedSize = sizeChange

        // Prevent new mark from going outside selection
        const boundedFrom = Math.max(from, pos)
        const boundedTo = Math.min(to, pos + node.nodeSize)

        if (currentSize) {
          tr.removeMark(boundedFrom, boundedTo, schema.marks.font_size)
        }
        tr.addMark(boundedFrom, boundedTo, schema.marks.font_size.create({ size: updatedSize }))
      }

      preserveSelection(tr, view.state)
    })
  }

  return tr
}

export const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 42, 48, 60, 72, 96]

export const fontSizeValidator = (fontSize: number): boolean => {
  return fontSize >= MIN_FONT_SIZE && fontSize <= MAX_FONT_SIZE
}

export function useSetFontSize() {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { activeFontSizes } = useContext(AlmanacEditorStateContext)!
  const [fontSizeInput] = useValidatedState(fontSizeValidator, activeFontSizes[0])
  const view = useEditorView()

  return (newSize: number, absolute = false) => {
    const { state } = view
    const tr = changeFontSize(view, newSize, absolute)

    const selection = state.selection

    if (selection instanceof NodeSelection && 'fontSize' in selection.node.attrs) {
      tr.setNodeMarkup(selection.from, undefined, { ...selection.node.attrs, fontSize: newSize })
    } else if (selection.empty && selection.$from.node().childCount === 0) {
      tr.setStoredMarks([
        ...(tr.storedMarks ?? []).filter((mark) => mark.type !== schema.marks.font_size),
        schema.marks.font_size.create({
          size: clamp(absolute ? newSize : fontSizeInput + newSize, MIN_FONT_SIZE, MAX_FONT_SIZE),
        }),
      ])
    }

    view.dispatch(tr)
    view.focus()
  }
}
